import { v4 as uuidv4 } from "uuid";
import { AppDispatch } from "src/store/store";
import {
  setCapturedImageSrc,
  setImageCapturingMode,
  setActiveObjectQuestions,
} from "src/store/inspectionCaptureSlice";
import {
  analyzeEvidenceAsync,
  setInspectionResult,
} from "src/store/inspectionResultsSlice";
import { createAIAcceptedResult, createOverrideResult, resetStreamingState as resetStreamingStateUtil } from "../utils";
import { initialResult } from "src/data/mockData";
import ImageStreamingWebSocketService from "src/services/imageStreamingWebSocketService";
import type { InspectionResult, AnalyzedResult, Question } from "../types";

interface HandleCaptureParams {
  captureFrame: () => Promise<File>;
  dispatch: AppDispatch;
  currentCreatedInspectionId: string;
  activeQuestion: any;
  activeObjectQuestions: any[];
  onOpenObjectSuccessSheet: (objectType: string, instanceNumber: number) => void;
  onOpenInspectionResultSheet: () => void;
}

interface HandleAcceptAIParams {
  dispatch: AppDispatch;
  activeQuestionId: string;
  inspectionResult: any;
  currentAnalyzedResult: any;
  capturedImageSrc: string;
  evidenceFile: File | string;
  streamingService: ImageStreamingWebSocketService | null;
  onClose: () => void;
  onBackToList?: () => void;
}

interface HandleOverrideParams {
  dispatch: AppDispatch;
  activeQuestionId: string;
  inspectionResult: any;
  currentAnalyzedResult: any;
  capturedImageSrc: string;
  evidenceFile: File | string;
  reasonCode: string;
  streamingService: ImageStreamingWebSocketService | null;
  onClose: () => void;
  onBackToList?: () => void;
}

/**
 * Unified capture handler for both object-based and normal questions
 */
export const handleCapture = async ({
  captureFrame,
  dispatch,
  currentCreatedInspectionId,
  activeQuestion,
  activeObjectQuestions,
  onOpenObjectSuccessSheet,
  onOpenInspectionResultSheet,
}: HandleCaptureParams) => {
  dispatch(setInspectionResult(initialResult));

  const file = await captureFrame();
  const url = URL.createObjectURL(file);
  dispatch(setCapturedImageSrc(url));

  if (!currentCreatedInspectionId) return;

  const analyzePayload = new FormData();
  analyzePayload.append("inspection_id", currentCreatedInspectionId);
  analyzePayload.append("file", file);

  // Determine if object-based or normal question
  const isObjectBased = activeObjectQuestions && activeObjectQuestions.length > 0;

  if (isObjectBased) {
    // Object-based: Show success sheet with uploading state
    const objectType = activeObjectQuestions[0]?.applicableObjectTypes?.[0] || "object";
    const instanceNumber = 1; // TODO: Calculate from existing results

    onOpenObjectSuccessSheet(objectType, instanceNumber);
    dispatch(setImageCapturingMode(false));

    // Upload questions
    activeObjectQuestions.forEach(q => {
      analyzePayload.append("question_ids", q.questionId);
    });

    // Call async API
    await dispatch(analyzeEvidenceAsync(analyzePayload)).unwrap();
  } else if (activeQuestion?.questionId) {
    // Normal question: Single question, show loading dialog
    analyzePayload.append("question_ids", activeQuestion.questionId);

    // Call async API - returns immediately with PENDING
    dispatch(analyzeEvidenceAsync(analyzePayload));

    // Show dialog with loading state
    onOpenInspectionResultSheet();
    dispatch(setImageCapturingMode(false));
  }
};

/**
 * Handle accepting AI answer and completing the question
 */
export const handleAcceptAI = ({
  dispatch,
  activeQuestionId,
  inspectionResult,
  currentAnalyzedResult,
  capturedImageSrc,
  evidenceFile,
  streamingService,
  onClose,
  onBackToList,
}: HandleAcceptAIParams) => {
  if (!activeQuestionId) return;

  const newResult = createAIAcceptedResult(
    inspectionResult,
    activeQuestionId,
    currentAnalyzedResult,
    capturedImageSrc,
    evidenceFile
  );

  dispatch(setInspectionResult(newResult));
  onClose();
  resetStreamingStateUtil(dispatch);

  // Clear object questions after completion
  dispatch(setActiveObjectQuestions([]));

  // Return to question selection list
  if (onBackToList) {
    onBackToList();
  }

  // Resume streaming if needed
  if (streamingService?.isConnected()) {
    setTimeout(() => {
      streamingService.resumeStreaming();
    }, 0);
  }
};

/**
 * Handle overriding AI answer and completing the question
 */
export const handleOverride = ({
  dispatch,
  activeQuestionId,
  inspectionResult,
  currentAnalyzedResult,
  capturedImageSrc,
  evidenceFile,
  reasonCode,
  streamingService,
  onClose,
  onBackToList,
}: HandleOverrideParams) => {
  if (!activeQuestionId) return;

  const newResult = createOverrideResult(
    inspectionResult,
    activeQuestionId,
    currentAnalyzedResult,
    capturedImageSrc,
    evidenceFile,
    reasonCode
  );

  dispatch(setInspectionResult(newResult));
  onClose();
  resetStreamingStateUtil(dispatch);

  // Clear object questions after completion
  dispatch(setActiveObjectQuestions([]));

  // Return to question selection list
  if (onBackToList) {
    onBackToList();
  }

  // Resume streaming if needed
  if (streamingService?.isConnected()) {
    setTimeout(() => {
      streamingService.resumeStreaming();
    }, 0);
  }
};
